import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Clock, Check } from "lucide-react";

export default function PublicBooking() {
  const { pageId } = useParams<{ pageId: string }>();
  const [page, setPage] = useState<any>(null);
  const [bookedSlots, setBookedSlots] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(false);

  useEffect(() => {
    if (!pageId) return;
    api.get(`/api/v1/booking-pages/${pageId}`)
      .then((data) => {
        setPage(data.page);
        setBookedSlots(data.bookedSlots || []);
      })
      .catch(() => toast.error("Booking page not found"))
      .finally(() => setLoading(false));
  }, [pageId]);

  const handleBook = async () => {
    if (!name || !email || !selectedDate || !selectedTime) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const startTime = `${selectedDate}T${selectedTime}:00`;
      const duration = page?.duration_minutes || 30;
      const endDate = new Date(new Date(startTime).getTime() + duration * 60000);
      const endTime = endDate.toISOString().slice(0, 19);

      await api.post("/api/v1/bookings", {
        bookingPageId: page.id,
        bookerName: name,
        bookerEmail: email,
        startTime,
        endTime,
        notes,
      });
      setBooked(true);
      toast.success("Meeting booked!");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
    </div>
  );

  if (!page) return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4">
      <h1 className="text-2xl font-bold">Booking Page Not Found</h1>
    </div>
  );

  if (booked) return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4">
      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
        <Check className="w-8 h-8 text-green-600" />
      </div>
      <h1 className="text-2xl font-bold">Meeting Booked!</h1>
      <p className="text-muted-foreground">You'll receive a confirmation email shortly.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-lg mx-auto px-4 py-12">
        <div className="bg-card rounded-2xl border border-border p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">{page.title}</h1>
              {page.description && <p className="text-sm text-muted-foreground">{page.description}</p>}
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Clock className="w-4 h-4" />
            <span>{page.duration_minutes || 30} minutes</span>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Date *</label>
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} min={new Date().toISOString().slice(0, 10)} className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium">Time *</label>
              <input type="time" value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium">Your Name *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium">Your Email *</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="john@example.com" className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium">Notes</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Any additional notes..." className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
            </div>
            <button onClick={handleBook} disabled={submitting} className="w-full py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-50 transition-opacity">
              {submitting ? "Booking..." : "Book Meeting"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
